import { Save as SaveIcon } from '@mui/icons-material'
import { Box, Chip, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useNotify, useSaveContext, useTranslate } from 'react-admin'
import { useFormContext, useFormState, useWatch } from 'react-hook-form'

// react-hook-form's dirtyFields is NOT "the keys that changed": after an array
// operation (a chronic condition added/removed) it is rebuilt with EVERY form
// key present and the unchanged ones set to `false` (RHF 7.68 getDirtyFields).
// A field is dirty only if its entry holds a `true` somewhere.
const isDirtyEntry = (entry: unknown): boolean =>
  entry === true ||
  (Array.isArray(entry)
    ? entry.some(isDirtyEntry)
    : typeof entry === 'object' && entry !== null && Object.values(entry).some(isDirtyEntry))

interface AutoSaveProps {
  debounce?: number
}

/**
 * AutoSave component that automatically saves form data after a debounce period.
 * Must be used inside a React Admin form (SimpleForm, TabbedForm, etc.)
 */
export const AutoSave = ({ debounce = 2000 }: AutoSaveProps) => {
  const { dirtyFields, isDirty, isSubmitting, isValidating } = useFormState()
  const { getValues, reset, setValue } = useFormContext()
  // Subscribe to VALUES, not just dirtiness. `isDirty`/`dirtyFields` do not
  // change when an already-dirty field is edited again, so without this the
  // effect below only re-ran when `isSaving` toggled — which is why a failing
  // save could only be retried by looping, and why suppressing the loop used to
  // drop the vet's next edit. The signature is a STRING so the dep is a
  // primitive (L-001): a new object identity every render would reset the
  // debounce forever and nothing would ever save.
  const watchedValues = useWatch()
  const valuesSignature = JSON.stringify(watchedValues ?? {})
  const { save } = useSaveContext()
  const notify = useNotify()
  const translate = useTranslate()
  const [isSaving, setIsSaving] = useState(false)
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastSavedValuesRef = useRef<any>(null)
  // Edit revision: bumps on every change of the form's values. The identical-
  // retry guard below holds only while NOTHING changed since the attempt it
  // recorded was SENT -- a failed value the vet re-enters is a new attempt
  // (TLA+ docs/spec/tla/PetEditAutosave.tla P2). Before, onError stored the
  // failed values and the guard then suppressed every later save of them.
  const editRevRef = useRef(0)
  const lastSavedRevRef = useRef(-1)
  useEffect(() => {
    editRevRef.current += 1
  }, [valuesSignature])

  useEffect(() => {
    // Skip if form is not dirty, is submitting, or is validating
    if (!isDirty || isSubmitting || isValidating || isSaving) {
      return
    }

    // Skip if there are no dirty fields
    if (Object.keys(dirtyFields).length === 0) {
      return
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Set a new timeout to save after debounce period
    timeoutRef.current = setTimeout(async () => {
      const values = getValues()
      const sendRev = editRevRef.current

      // Skip if values haven't actually changed since last save -- and nothing
      // was edited since that attempt was sent.
      if (
        lastSavedValuesRef.current &&
        lastSavedRevRef.current === sendRev &&
        JSON.stringify(values) === JSON.stringify(lastSavedValuesRef.current)
      ) {
        return
      }

      setIsSaving(true)
      try {
        if (save) {
          // Filter out nested objects that shouldn't be saved (like 'owner')
          // and only save pet-specific fields.
          //
          // ⚠️ This list is NOT derived from the form, so a field added to
          // PetEdit without being added here is silently dropped — and because
          // onSuccess below fires a "saved" notify and resets the form clean,
          // the vet is told it worked. That is exactly what happened to the
          // chronic-care panel (eb0c619a, 2026-06-21): eight clinical fields
          // were unsaveable for five weeks. `AutoSave.test.tsx` now pins this
          // list against every `source=` PetEdit renders — keep them in step.
          const petFields = {
            name: values.name,
            species: values.species,
            breed: values.breed,
            dob: values.dob,
            gender: values.gender,
            height: values.height,
            weight: values.weight,
            // Chronic-care profile. Consumed downstream by ada-core
            // Pets/calorieSummary.ts and AdapetApp's petProfileScreen /
            // CalorieSummary, so these are the vet's only way to set them.
            weight_kg: values.weight_kg,
            neutered: values.neutered,
            // NOTE: birth_date is a Payload `date` (ISO string), NOT Unix
            // seconds like `dob` — do not apply the dob converter to it.
            birth_date: values.birth_date,
            body_condition_score: values.body_condition_score,
            chronic_conditions: values.chronic_conditions,
            target_glucose_low: values.target_glucose_low,
            target_glucose_high: values.target_glucose_high,
            target_kcal_in_override: values.target_kcal_in_override,
            // Vet-only display control: hide numeric glucose from the owner
            // app (charts/alerts go qualitative). Vet-writable/owner-readable,
            // enforced field-side in ada-core Pets.
            hide_glucose_values: values.hide_glucose_values,
          }

          // Send ONLY what the vet changed. The whole allowlisted set used to go
          // out on every autosave, so a field another writer changed since the
          // form loaded -- the owner's app PATCHes weight_kg, neutered and
          // birth_date (AdapetApp updatePetHealth) -- was written back with the
          // form's stale copy (TLA+ PetEditAutosave P1). `petFields` stays the
          // single list of what MAY be saved (AutoSave.test.tsx pins it).
          const changed = Object.fromEntries(
            Object.entries(petFields).filter(([field]) => isDirtyEntry(dirtyFields[field])),
          )

          await save(changed, {
            onSuccess: () => {
              lastSavedValuesRef.current = values
              lastSavedRevRef.current = sendRev
              setLastSavedTime(new Date())
              // `values` was snapshotted BEFORE the await. Resetting to it
              // unconditionally overwrote anything the vet typed while the
              // request was open AND marked the form clean, so the newer edit
              // was neither kept nor sent — a silent lost update behind a
              // "Saved" toast. Only reset when nothing has changed since the
              // snapshot; otherwise leave the form dirty so the effect below
              // schedules a follow-up save for the newer values.
              const current = getValues()
              // What the server now holds is `values`: make it the form's
              // baseline. Anything the vet changed since the snapshot is put
              // back ON TOP, dirty -- including a field turned back to its
              // LOADED value mid-save, which against the old baseline looked
              // clean, was never re-sent, and was then overwritten on screen by
              // the refreshed record.
              reset(values, { keepDirty: false, keepDirtyValues: false })
              for (const [field, value] of Object.entries(current)) {
                if (JSON.stringify(value) !== JSON.stringify(values[field])) {
                  setValue(field, value, { shouldDirty: true })
                }
              }
              notify(translate('pos.pet_edit.auto_save.saved'), { type: 'success' })
            },
            onError: (error: any) => {
              console.error('AutoSave error:', error)
              // Record the attempt so the equality guard at the top of the
              // timeout suppresses an IDENTICAL retry. Safe now that the effect
              // is subscribed to `valuesSignature`: a NEW edit changes the
              // signature, re-runs the effect and re-arms the save, so this
              // stops the 2s failure loop without dropping the vet's next edit.
              // (eng-brain P24-3.)
              lastSavedValuesRef.current = values
              lastSavedRevRef.current = sendRev
              notify(translate('pos.pet_edit.auto_save.error'), { type: 'error' })
            },
          })
        }
      } catch (error) {
        console.error('AutoSave error:', error)
        notify(translate('pos.pet_edit.auto_save.error'), { type: 'error' })
      } finally {
        setIsSaving(false)
      }
    }, debounce)

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [
    isDirty,
    dirtyFields,
    isSubmitting,
    isValidating,
    debounce,
    getValues,
    save,
    notify,
    translate,
    reset,
    isSaving,
    valuesSignature,
  ])

  // Don't render anything if form hasn't been modified
  if (!isDirty && !isSaving && !lastSavedTime) {
    return null
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {isSaving && (
        <Chip
          label={translate('pos.pet_edit.auto_save.saving')}
          color="info"
          size="small"
          icon={<SaveIcon />}
        />
      )}
      {isDirty && !isSaving && (
        <Chip
          label={translate('pos.pet_edit.auto_save.unsaved_changes')}
          color="warning"
          size="small"
          variant="outlined"
        />
      )}
      {lastSavedTime && !isDirty && !isSaving && (
        <Typography variant="caption" color="text.secondary">
          {translate('pos.pet_edit.auto_save.last_saved', {
            time: lastSavedTime.toLocaleTimeString(),
          })}
        </Typography>
      )}
    </Box>
  )
}

export default AutoSave
